<script setup lang="ts">
import { cloneDeep } from 'lodash-es';
import { computed, ref, watch } from 'vue';
import { useTemplateRefsList } from '@vueuse/core';

import { AssetType } from '@/helpers/const';
import { getFullName } from '@/helpers/format';
import { toast } from '@/helpers/toast';
import { useCustomerQuoteProcess } from '@/helpers/useCustomerQuoteProcess';

import AssetForm from './modules/Assets/AssetForm.vue';
import SimpleLiability from './modules/Liabilities/SimpleLiability.vue';
// import SimpleComprehensive from './modules/SimpleComprehensive.vue';

import type { ApplicantId, PropertyAsset } from '@/types';

const { loanApp, loanApplicants, sectionStates, saveAndGoNext, goBack } = useCustomerQuoteProcess();

const assetsLiabilitiesType = computed(() => sectionStates.value.assetsLiabilities);

const validApplicants = computed(() => {
  if (assetsLiabilitiesType.value === 'simple') {
    return [loanApp.value.applicants[0].id];
  }
  return loanApp.value.applicants.map((a) => a.id);
});

const financials = ref(cloneDeep(loanApp.value.financials));
const activeTab = ref<ApplicantId | undefined>(
  validApplicants.value.length === 1 ? validApplicants.value[0] : undefined,
);
const assetRefs = useTemplateRefsList<InstanceType<typeof AssetForm>>();
const simpleLiabilityRefs = useTemplateRefsList<InstanceType<typeof SimpleLiability>>();

const validateAssets = async () => {
  let isValid = true;

  if (assetsLiabilitiesType.value === 'simple') {
    const refCount = simpleLiabilityRefs.value.length;
    for (let i = 0; i < refCount; i++) {
      isValid = (await simpleLiabilityRefs.value[i].update()) ?? false;
      if (!isValid) {
        window.scrollTo(0, 0);
        break;
      }
    }
  } else {
    const refCount = assetRefs.value.length;
    for (let i = 0; i < refCount; i++) {
      isValid = (await assetRefs.value[i].update()) ?? false;
      if (!isValid) {
        window.scrollTo(0, 0);
        break;
      }
    }

    const addressLines = Object.values(financials.value).flatMap((applicant) =>
      applicant.assets
        .filter((asset) => asset.type === 'property' && asset.address)
        .map((asset) => (asset as PropertyAsset).address.addressLine),
    );

    const hasDuplicateAddress = addressLines.some((addressLine, index) => addressLines.indexOf(addressLine) !== index);
    if (hasDuplicateAddress) {
      isValid = false;
      window.scrollTo(0, 0);
      toast({
        type: 'danger',
        title: 'Error',
        description: 'Please only enter a property asset once.',
      });
    }
  }

  return isValid;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const cardTitle = (item: any) => {
  let description = '';
  if (item.type === AssetType.PROPERTY) {
    description = item.address?.addressLine;
  } else if (item.institution) {
    description = item.institution;
  } else if (item.type === AssetType.MOTOR_VEHICLES && item.model) {
    description = `${item.make ?? ''} ${item.model}`.trim();
  } else if ((item.type === AssetType.MACHINERY || item.type === AssetType.EQUIPMENT) && item.description) {
    description = item.description;
  } else if (item.details) {
    description = item.details;
  }

  return `${item.type.charAt(0).toUpperCase() + item.type.slice(1).replace('_', ' ')}: ${description}`;
};

const cardDescription = (applicantId: ApplicantId) => {
  const assets = financials.value[applicantId].assets.map((item) => {
    return cardTitle(item);
  });
  const liabilities = financials.value[applicantId].liabilities.map((item) => {
    return cardTitle(item);
  });

  return [...assets, ...liabilities];
};

const saveAndContinue = async () => {
  if (await validateAssets()) {
    saveAndGoNext({
      financials: financials.value,
    });
  }
};

watch(
  assetsLiabilitiesType,
  (newType, oldType) => {
    const isRelevantChange =
      (newType === 'simple' && oldType === 'comprehensive') || (newType === 'comprehensive' && oldType === 'simple');

    if (!isRelevantChange) return;

    for (const applicantId of validApplicants.value) {
      financials.value[applicantId].assets = [];
      financials.value[applicantId].liabilities = [];
    }
  },
  { immediate: true },
);
</script>

<template>
  <div class="w-full">
    <div class="mb-4 pb-2">
      <h2 class="text-2xl font-medium">
        Tell us about your
        <BxHighlight>liabilities</BxHighlight>
      </h2>
    </div>
    <div class="my-6">
      <p>
        Enter your total monthly repayment amount across all liabilities, this should include your credit cards, motor
        vehicles, and personal loans.
      </p>
    </div>
    <!-- <div class="my-4">
      <SimpleComprehensive :section-key="'assetsLiabilities'" title="Assets" />
      <p class="pt-4">
        Use the Quick vs Comprehensive toggle to choose whether you want to enter just the total monthly liability
        repayments or a full breakdown of assets and liabilities.
      </p>
    </div> -->
    <div class="my-6 grid gap-4">
      <div v-for="applicantId in validApplicants" :key="applicantId">
        <div v-if="assetsLiabilitiesType === 'simple'">
          <BxCard>
            <h3 class="mb-8 flex items-center text-lg font-medium leading-6 text-gray-900">
              <span class="mr-3 hidden md:block"><BxIcon name="liabilities" /></span>
              <span>{{ getFullName(loanApplicants[applicantId]) }}</span>
            </h3>
            <SimpleLiability
              :ref="simpleLiabilityRefs.set"
              v-model:liabilities="financials[applicantId].liabilities"
              :applicant-id="applicantId"
            />
          </BxCard>
        </div>
        <div v-else>
          <BxCard :class="applicantId !== activeTab && 'hidden'">
            <h2 class="mb-4 text-xl font-medium">
              {{ getFullName(loanApplicants[applicantId]) }}
            </h2>
            <AssetForm
              :ref="assetRefs.set"
              v-model:assets="financials[applicantId].assets"
              v-model:liabilities="financials[applicantId].liabilities"
              :applicant-id="applicantId"
              :animate="applicantId === activeTab"
              :show-buttons="validApplicants.length > 1"
              @save="activeTab = undefined"
            />
          </BxCard>
          <BxCard :class="`${applicantId === activeTab && 'hidden'} asset-card`">
            <div class="flex items-center">
              <div class="card-details">
                <div class="mb-2 font-medium">{{ getFullName(loanApplicants[applicantId]) }}</div>
              </div>
              <div class="asset-descriptions ml-6">
                <div v-for="description in cardDescription(applicantId)" :key="description">
                  <span class="text-gray-400">{{ description }}</span>
                </div>
              </div>

              <div class="card-button">
                <BxButton
                  :id="applicantId + '-assets-start-add'"
                  variant="secondary"
                  :disabled="activeTab && activeTab !== applicantId"
                  @click="activeTab = applicantId"
                >
                  {{ financials[applicantId].assets.length ? 'Edit' : 'Add' }} assets
                </BxButton>
              </div>
            </div>
          </BxCard>
          <BxCard :class="`${applicantId === activeTab && 'hidden'} asset-card-mobile`">
            <div class="items-left flex flex-col">
              <div class="card-details">
                <div class="mb-2 font-medium">{{ getFullName(loanApplicants[applicantId]) }}</div>
              </div>
              <div class="asset-descriptions ml-6">
                <div v-for="description in cardDescription(applicantId)" :key="description">
                  <span class="text-gray-400">{{ description }}</span>
                </div>
              </div>
            </div>
            <div class="card-button">
              <BxButton
                :id="applicantId + '-assets-start-add'"
                variant="secondary"
                :disabled="activeTab && activeTab !== applicantId"
                @click="activeTab = applicantId"
              >
                {{ financials[applicantId].assets.length ? 'Edit' : 'Add' }} assets
              </BxButton>
            </div>
          </BxCard>
        </div>
      </div>
    </div>

    <div class="mt-6">
      <BxButton id="assets-continue" variant="primary" @click="saveAndContinue()">Save and continue</BxButton>
      <BxButton variant="link" class="ml-4" @click="goBack()">Back</BxButton>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.section-panel {
  --border-radius: 0.5rem;
  --border-color: #ccc;

  border: 1px solid var(--border-color);
  border-radius: var(--border-radius);

  background-color: #fff;
}

.asset-descriptions {
  @media (max-width: 767px) {
    margin-top: 20px;
    margin-bottom: 20px;
  }
}

.asset-card {
  @media (max-width: 767px) {
    display: none;
  }
}

.asset-card-mobile {
  @media (min-width: 767px) {
    display: none;
  }
}

.card-button {
  flex-grow: 1;
  @media (min-width: 767px) {
    text-align: right;
  }
  @media (max-width: 767px) {
    text-align: center;
  }
  flex-basis: 200px;
}
</style>
